/**
 * Paid-event payment mode helpers (Payment Link vs integrated Stripe Checkout).
 * Mirror of server/utils/eventPaymentMode.js — keep predicates aligned.
 */

export const PAYMENT_MODE_PAYMENT_LINK = 'payment_link';
export const PAYMENT_MODE_INTEGRATED = 'integrated';

export function isPaidEvent(event) {
  if (!event || typeof event !== 'object') return false;
  return (event.event_type || 'Free') === 'Paid'
    && event.price_cents != null
    && Number(event.price_cents) > 0;
}

/** @returns {'payment_link'|'integrated'|null} */
export function getPaymentMode(event) {
  if (!isPaidEvent(event)) return null;
  const mode = event.payment_mode != null ? String(event.payment_mode).trim() : '';
  if (mode === PAYMENT_MODE_PAYMENT_LINK || mode === PAYMENT_MODE_INTEGRATED) return mode;
  return null;
}

export function getPaymentLinkUrl(event) {
  if (!event) return '';
  return event.payment_link_url != null ? String(event.payment_link_url).trim() : '';
}

export function isPaymentLinkPaid(event) {
  return getPaymentMode(event) === PAYMENT_MODE_PAYMENT_LINK && !!getPaymentLinkUrl(event);
}

export function isIntegratedPaid(event) {
  return getPaymentMode(event) === PAYMENT_MODE_INTEGRATED;
}

export function blocksPaidBulkRegistration(event) {
  return isPaidEvent(event) && !isPaymentLinkPaid(event);
}

export function allowsManualPaymentStatus(event) {
  return getPaymentMode(event) === PAYMENT_MODE_PAYMENT_LINK;
}

/** Manual settlement targets (not Pending / NA). Mirror server/utils/eventPaymentMode.js. */
export const MANUAL_SETTLED_PAYMENT_STATUSES = ['Paid', 'Sponsored'];

export function isManualSettledPaymentStatus(paymentStatus) {
  const status = paymentStatus != null ? String(paymentStatus).trim() : '';
  return MANUAL_SETTLED_PAYMENT_STATUSES.includes(status);
}

export function isAllowedManualPaymentStatusValue(paymentStatus) {
  const status = paymentStatus != null ? String(paymentStatus).trim() : '';
  return status === 'Pending' || isManualSettledPaymentStatus(status);
}

export function isValidHttpUrl(value) {
  const s = value != null ? String(value).trim() : '';
  if (!s) return false;
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch (_) {
    return false;
  }
}

function formatPriceDollarsFromCents(priceCents) {
  if (priceCents == null || Number.isNaN(Number(priceCents))) return '';
  return (Number(priceCents) / 100).toFixed(2);
}

/**
 * Card / table / detail display: "Free" or "$12.00" for paid events with price > 0.
 * @param {{ event_type?: string, price_cents?: number }|null|undefined} event
 */
export function formatEventPriceLabel(event) {
  if (isPaidEvent(event)) {
    return `$${formatPriceDollarsFromCents(event.price_cents)}`;
  }
  return 'Free';
}
